import React, { useEffect, useRef } from "react";
import { useResult } from "../formContext/hooks/useResult";
import { utils } from "../../helpers";

// Wait this long after the LAST resize event before scrolling.
// This helps avoid scrolling too early while content is still growing.
const QUIET_PERIOD_MS = 250;

// Safety timeout.
// If the element keeps resizing forever (images, animations, etc.),
// we'll scroll anyway after 4 seconds.
const MAX_WAIT_MS = 4000;

const OnLoadScrollDesktop: React.FC = () => {
  const { resultData } = useResult();

  const response = resultData?.response;

  // Stores the response we already scrolled for.
  // Prevents scrolling multiple times for the same response.
  const scrolledForResponseRef = useRef<unknown>(null);

  useEffect(() => {
    // Don't do anything if:
    // 1. No response exists yet
    // 2. We've already scrolled for this response
    if (!response || scrolledForResponseRef.current === response) return;

    // Skip on article/page contexts; on mobile, only scroll to the results when the visitor actually clicked submit themselves
    if (!utils.shouldAutoScrollToToolBlock(resultData?.userDriven)) return;

    // Indicates whether scrolling has already happened
    let finished = false;

    // The DOM element we'll eventually scroll to
    let resolvedTarget: HTMLElement | null = null;

    // requestAnimationFrame id
    let findTargetFrame: number | null = null;

    // Timer used to detect "quiet period"
    let quietTimer: ReturnType<typeof setTimeout> | null = null;

    // Hard timeout timer
    let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;

    // Watches element size changes
    let resizeObserver: ResizeObserver | null = null;

    // Collection of cleanup callbacks
    const cleanupFns: Array<() => void> = [];

    /**
     * Cleans up all observers, timers and listeners.
     * Called either when scrolling completes or effect unmounts.
     */
    const cleanup = () => {
      if (findTargetFrame !== null) {
        cancelAnimationFrame(findTargetFrame);
      }

      if (quietTimer) {
        clearTimeout(quietTimer);
      }

      if (maxWaitTimer) {
        clearTimeout(maxWaitTimer);
      }

      resizeObserver?.disconnect();

      cleanupFns.forEach((fn) => fn());
      cleanupFns.length = 0;
    };

    /**
     * Final step:
     * - prevent duplicate execution
     * - clean everything up
     * - remember we already handled this response
     * - scroll the target into view
     */
    const finish = () => {
      if (finished) return;

      finished = true;

      cleanup();

      scrolledForResponseRef.current = response;

      resolvedTarget?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    /**
     * Called whenever something changes size.
     *
     * We reset the timer every time.
     * Only after 250ms of NO resize activity do we scroll.
     */
    const scheduleQuietCheck = () => {
      if (finished) return;

      if (quietTimer) {
        clearTimeout(quietTimer);
      }

      quietTimer = setTimeout(finish, QUIET_PERIOD_MS);
    };

    /**
     * Attempts to find the results container.
     *
     * Since the DOM might not be rendered yet,
     * we keep retrying using requestAnimationFrame.
     */
    const start = () => {
      const target = utils.resolveResultsTarget();

      // If not found yet, try again next frame
      if (!target) {
        findTargetFrame = requestAnimationFrame(start);
        return;
      }

      // Store the element we'll scroll to
      resolvedTarget = target;

      /**
       * Watch for size changes.
       * - content rendering
       * - async components loading
       * - images affecting layout
       */
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(scheduleQuietCheck);
        resizeObserver.observe(target);
      }

      /**
       * Images often load after initial render and
       * can change the height of the container.
       */
      target.querySelectorAll("img").forEach((img) => {
        // Skip already loaded images
        if (img.complete) return;

        const onDone = () => scheduleQuietCheck();

        img.addEventListener("load", onDone);
        img.addEventListener("error", onDone);

        cleanupFns.push(() => {
          img.removeEventListener("load", onDone);
          img.removeEventListener("error", onDone);
        });
      });

      /**
       * Wait for fonts.
       * Font loading can change text dimensions, which can affect layout height.
       */
      document.fonts?.ready?.then(scheduleQuietCheck).catch(() => {});

      /**
       * Absolute fallback:
       * scroll after 4 seconds no matter what.
       */
      maxWaitTimer = setTimeout(finish, MAX_WAIT_MS);

      /**
       * Start the first quiet-period countdown.
       * If nothing changes for 250ms, we'll scroll.
       */
      scheduleQuietCheck();
    };

    // Kick off DOM lookup on next paint
    findTargetFrame = requestAnimationFrame(start);

    // Cleanup when component unmounts
    // or when response changes
    return cleanup;
  }, [response, resultData?.userDriven]);

  return null;
};

export default OnLoadScrollDesktop;
